// src/pages/info/ai-context.tsx
import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function AiContextPage() {
  return (
    <div className="container mx-auto max-w-4xl py-8 px-4">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-4">
          <h1 className="text-4xl font-bold">AI Agent Context</h1>
          <p className="text-xl text-muted-foreground">
            Comprehensive information about 8020agent for AI systems and agents
          </p>
        </div>

        {/* Introduction */}
        <Card>
          <CardHeader>
            <CardTitle>About This Page</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              This page provides structured information about 8020agent for AI
              systems, language models, and AI agents. It's designed to help AI
              assistants understand our platform, products, and how to help
              users effectively.
            </p>
            <div className="space-y-2">
              <p className="font-semibold">For AI Agents:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <a
                    href="/llms.txt"
                    className="text-primary hover:underline"
                    target="_blank"
                  >
                    /llms.txt
                  </a>{" "}
                  - Primary LLM context file (markdown format)
                </li>
                <li>
                  <a
                    href="/agents.txt"
                    className="text-primary hover:underline"
                    target="_blank"
                  >
                    /agents.txt
                  </a>{" "}
                  - Agent integration guide
                </li>
                <li>
                  <a
                    href="/sitemap.xml"
                    className="text-primary hover:underline"
                    target="_blank"
                  >
                    /sitemap.xml
                  </a>{" "}
                  - Complete site structure
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Platform Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Platform Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">
                What is 8020agent?
              </h3>
              <p>
                8020agent is a free Google Ads optimization tool that helps
                advertisers maximize profit through AI-powered insights, budget
                optimization, and automated reporting. Unlike traditional tools
                that focus on platform metrics, we emphasize profit
                maximization.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">Key Features</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Comprehensive Google Ads reporting and analysis</li>
                <li>AI-powered insights (OpenAI, Claude, Gemini)</li>
                <li>Budget optimization with profit curves</li>
                <li>Keyword and search term analysis</li>
                <li>Performance Max placement and asset analysis</li>
                <li>Quality score recommendations</li>
                <li>Hourly performance trends</li>
                <li>Product performance with n-gram analysis</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">
                Technology & Privacy
              </h3>
              <p>
                All user data is stored client-side in the browser using
                IndexedDB. We don't store your Google Ads data on our servers,
                ensuring complete privacy and control.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Products */}
        <Card>
          <CardHeader>
            <CardTitle>Products & Services</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">
                Free 8020agent Tool
              </h3>
              <p>
                Complete Google Ads analysis and optimization - no credit card
                required. Get started at{" "}
                <Link to="/" className="text-primary hover:underline">
                  8020agent.com
                </Link>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">
                Performance Max Scripts
              </h3>
              <p>
                Automated Google Ads Scripts for reporting and optimization.
                Single account ($199) or MCC license ($399).{" "}
                <Link to="/scripts" className="text-primary hover:underline">
                  Learn more about scripts
                </Link>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">
                Ads to AI Community
              </h3>
              <p>
                Premium membership (€799/year) for Google Ads professionals
                transitioning to AI automation. Includes advanced training,
                exclusive tools, and community access.{" "}
                <Link to="/ai" className="text-primary hover:underline">
                  Join the community
                </Link>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">
                Build the Agent Bootcamp
              </h3>
              <p>
                Intensive cohort-based training (€699) teaching Google Ads pros
                how to build custom AI agents.{" "}
                <Link to="/build" className="text-primary hover:underline">
                  Enroll in bootcamp
                </Link>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">
                Scripts & Sheets Mastery
              </h3>
              <p>
                Training on Google Ads Scripts and Sheets automation - the
                first step toward AI.{" "}
                <Link to="/mastery" className="text-primary hover:underline">
                  View workshop details
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* About Mike Rhodes */}
        <Card>
          <CardHeader>
            <CardTitle>About Mike Rhodes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Mike Rhodes is the founder and CEO of 8020agent, based in
              Melbourne, Australia. He specializes in Google Ads automation,
              AI training, and building communities for digital marketing
              professionals.
            </p>

            <div>
              <h3 className="font-semibold text-lg mb-2">Expertise</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Google Ads optimization and automation</li>
                <li>Explaining complex concepts simply</li>
                <li>Public speaking and training</li>
                <li>Building practical AI solutions</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">Philosophy</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Show working tools, not just theory</li>
                <li>Bridge from Google Ads Scripts to AI</li>
                <li>Focus on profit, not platform metrics</li>
                <li>Practical solutions over AI hype</li>
                <li>Value-based pricing aligned with ROI</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Common Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Common Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold">Getting Started</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                <li>
                  <strong>How do I connect my data?</strong> Use our Google
                  Sheets template and Apps Script integration
                </li>
                <li>
                  <strong>Is it really free?</strong> Yes, the core tool is
                  completely free
                </li>
                <li>
                  <strong>Where is my data stored?</strong> Client-side in your
                  browser only
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mt-4">AI Integration</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                <li>
                  <strong>Which AI models are supported?</strong> OpenAI GPT,
                  Anthropic Claude, Google Gemini
                </li>
                <li>
                  <strong>Can I use my own API keys?</strong> Yes, configure in
                  Settings
                </li>
                <li>
                  <strong>Is AI analysis included for free?</strong> Yes, with
                  your own API keys
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mt-4">Products</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                <li>
                  <strong>What's the value ladder?</strong> Free tool →
                  Scripts ($199-399) → Community (€799/year) → Bootcamp (€699)
                </li>
                <li>
                  <strong>Do I need scripts?</strong> No, the free tool works
                  independently
                </li>
                <li>
                  <strong>What is Ads to AI?</strong> Premium community for
                  advanced AI training
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Technical Details */}
        <Card>
          <CardHeader>
            <CardTitle>Technical Architecture</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Frontend</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>React 18 with TypeScript</li>
                <li>Vite build system</li>
                <li>Tailwind CSS + Radix UI components</li>
                <li>D3.js, Recharts, AmCharts for visualizations</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">Data & Auth</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>IndexedDB via Dexie.js for client-side storage</li>
                <li>Clerk for authentication and organization management</li>
                <li>Google Sheets for data import</li>
                <li>Sample data mode for demonstrations</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">AI Integration</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Replit backend service for API proxying</li>
                <li>Support for user-provided API keys</li>
                <li>Multiple AI provider support</li>
                <li>Customizable analysis prompts</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Key Pages */}
        <Card>
          <CardHeader>
            <CardTitle>Important Pages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2">Main Features</h3>
                <ul className="space-y-1">
                  <li>
                    <Link to="/" className="text-primary hover:underline">
                      Home / Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/analysis"
                      className="text-primary hover:underline"
                    >
                      Budget Optimization
                    </Link>
                  </li>
                  <li>
                    <Link to="/trends" className="text-primary hover:underline">
                      Performance Trends
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/keywords"
                      className="text-primary hover:underline"
                    >
                      Keyword Analysis
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products"
                      className="text-primary hover:underline"
                    >
                      Product Performance
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/settings"
                      className="text-primary hover:underline"
                    >
                      Settings & API Keys
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Sales & Info</h3>
                <ul className="space-y-1">
                  <li>
                    <Link
                      to="/scripts"
                      className="text-primary hover:underline"
                    >
                      Scripts Products
                    </Link>
                  </li>
                  <li>
                    <Link to="/buy" className="text-primary hover:underline">
                      Buy Scripts
                    </Link>
                  </li>
                  <li>
                    <Link to="/ai" className="text-primary hover:underline">
                      Ads to AI Community
                    </Link>
                  </li>
                  <li>
                    <Link to="/build" className="text-primary hover:underline">
                      Build the Agent Bootcamp
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/mastery"
                      className="text-primary hover:underline"
                    >
                      Scripts & Sheets Mastery
                    </Link>
                  </li>
                  <li>
                    <Link to="/help" className="text-primary hover:underline">
                      Help & Documentation
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              This page is designed to be read by AI agents and assistants.
              For regular help documentation, visit{" "}
              <Link to="/help" className="text-primary hover:underline">
                /help
              </Link>
              . For privacy information, see{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                /privacy
              </Link>
              .
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Last updated: October 2025
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
